import { Alert, Autocomplete, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import EnglishFormService from '../../services/EnglishFormService';
import TaskTrackingService, { taskErrorMessage } from '../../services/TaskTrackingService';
import CopyButton from '../work/CopyButton';
import { shortName } from './taskUi';

const FORMS = [
    { kind: 'REQUEST', label: 'Request link', help: 'AV asks for English subtitles. The request becomes a waiting task for the person it goes to.' },
    { kind: 'SRT', label: 'SRT link', help: 'AV sends a finished English SRT. It goes to English SRTs under Tasks.' },
];

const linkUrl = (token) => `${window.location.origin}/r/${token}`;

const FormLink = ({ form, link, pms, onChanged, onError }) => {
    const [coordinator, setCoordinator] = useState(null);
    const [busy, setBusy] = useState(false);

    const act = async (fn) => {
        setBusy(true);
        onError('');
        try {
            await fn();
            setCoordinator(null);
            onChanged();
        } catch (e) {
            onError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box>
            <Typography variant="subtitle2">{form.label}</Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                {form.help}
            </Typography>
            {link ? (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Link href={linkUrl(link.token)} target="_blank" rel="noopener" variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                            {linkUrl(link.token)}
                        </Link>
                        <CopyButton text={linkUrl(link.token)} label="Copy link" ariaLabel={`Copy ${form.label}`} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Requests go to: {shortName(link.coordinator)}
                    </Typography>
                    {link.coordinatorLost && (
                        <Alert severity="warning" sx={{ mt: 0.5 }}>
                            {shortName(link.coordinator)} is no longer a PM, so this form says it's closed. Turn it off and make a new one.
                        </Alert>
                    )}
                    <Button size="small" color="inherit" sx={{ mt: 0.5 }} disabled={busy} onClick={() => act(() => EnglishFormService.turnOff(link.id))}>
                        Turn off
                    </Button>
                </>
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Autocomplete
                        size="small"
                        sx={{ flex: 1, minWidth: 220 }}
                        options={pms}
                        value={coordinator}
                        getOptionLabel={(u) => u.name || u.email}
                        isOptionEqualToValue={(a, b) => a.email === b.email}
                        onChange={(_, u) => setCoordinator(u)}
                        renderInput={(params) => <TextField {...params} label="Requests go to" />}
                    />
                    <Button
                        variant="outlined"
                        disabled={busy || !coordinator}
                        onClick={() => act(() => EnglishFormService.createLink(form.kind, coordinator.email))}
                    >
                        Make link
                    </Button>
                </Box>
            )}
        </Box>
    );
};

/** Task tools' form links: one live link of each kind. */
const EnglishFormsSection = () => {
    const [links, setLinks] = useState(null);
    const [pms, setPms] = useState([]);
    const [error, setError] = useState('');

    const load = () =>
        EnglishFormService.links()
            .then(setLinks)
            .catch((e) => setError(taskErrorMessage(e)));

    useEffect(() => {
        load();
        TaskTrackingService.assignableUsers()
            .then((users) => setPms(users.filter((u) => u.projectManager)))
            .catch(() => setPms([]));
    }, []);

    return (
        <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {links &&
                FORMS.map((form) => (
                    <FormLink
                        key={form.kind}
                        form={form}
                        link={links.find((l) => l.kind === form.kind)}
                        pms={pms}
                        onChanged={load}
                        onError={setError}
                    />
                ))}
        </Stack>
    );
};

export default EnglishFormsSection;
